import { BridgeClient } from 'app/service/bridge/BridgeClient';
import {
  notifyIndexingRequested,
  notifyIndexingRequestFailed,
  notifyReindexRequired,
} from 'features/operation-supervisor/indexingRequestEvents';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';

/**
 * Creates an indexing API backed by the bridge transport.
 *
 * Bridge actions used:
 *   indexing.history  → BridgeUseCase → IndexingService (recent job list)
 *   indexing.start    → BridgeUseCase → IndexingService (full reindex job)
 *
 * Note: indexing.start emits an app-local request event before the bridge call
 * so status guards can block index-dependent settings immediately, before the
 * next onboarding.state poll observes the queued job.
 *
 * Response normalisation:
 *   Bridge/plugin may return history under response.data.history or response.history.
 *   getHistory() always resolves to { history: Array<Job> } regardless of
 *   which shape the plugin sends.
 *
 * Error behaviour:
 *   Bridge protocol errors (result === 'error') are surfaced as thrown Errors
 *   so the caller can catch and display a localised message.
 *
 * Added in Task 10I: extracted from DataSettingsEmbedded.jsx to make the
 * component transport-agnostic (no direct BridgeClient in UI code).
 */
export function createBridgeIndexingApi() {
  const client = new BridgeClient();

  const start = async (action, source) => {
    notifyIndexingRequested({ source });
    let response;
    try {
      response = await client.sendMessageWithResponse(action, {});
    } catch (err) {
      notifyIndexingRequestFailed({
        source,
        error: err?.message || getLocalizedMessage('catalog.update.startIndexingFailed'),
      });
      throw err;
    }
    if (response?.result === 'error') {
      const message = response?.error || getLocalizedMessage('catalog.update.startIndexingFailed');
      notifyIndexingRequestFailed({ source, error: message });
      throw new Error(message);
    }
    const rawJobId = response?.job_id || response?.data?.job_id || null;
    return {
      jobId: rawJobId == null ? null : String(rawJobId),
      indexing: response?.data?.indexing || response?.indexing || null,
    };
  };

  return {
    /**
     * Fetches recent indexing job history from the plugin.
     *
     * @param {number} [limit=10]  Max number of job entries to return.
     * @returns {Promise<{ history: Array }>}
     */
    async getHistory(limit = 10) {
      const response = await client.sendMessageWithResponse(
        'indexing.history',
        { limit },
      );
      if (response?.result === 'error') {
        throw new Error(response?.error || getLocalizedMessage('search.data.indexing.historyLoadFailed'));
      }
      const list = response?.data?.history ?? response?.history ?? [];
      return { history: Array.isArray(list) ? list : [] };
    },

    /**
     * Starts a full indexing job after index-dependent settings change.
     *
     * @returns {Promise<{ jobId: string|null, indexing: object|null }>}
     */
    async startFull() {
      return start('indexing.start', 'bridge-indexing-api');
    },

    /** Persists a pending catalog rebuild without starting a worker job. */
    async markRequired() {
      const response = await client.sendMessageWithResponse('indexing.mark_required', {});
      if (response?.result === 'error') {
        throw new Error(response?.error || getLocalizedMessage('catalog.update.markRequiredFailed'));
      }
      notifyReindexRequired({ source: 'bridge-indexing-api' });
      return { reindexRequired: response?.reindexRequired === true };
    },

    /**
     * Canonical full-index action used by merchandising. Existing settings
     * consumers keep startFull()/indexing.start as the compatibility lane.
     */
    async startCanonicalFull() {
      const source = 'bridge-merchandising-indexing-api';
      const started = await start('index.startFull', source);

      // index.startFull durably enqueues the job. The worker dispatch is a
      // separate canonical bridge action, so the merchandising workspace must
      // kick the exact returned job instead of leaving it queued indefinitely.
      if (!started.jobId) return started;

      let response;
      try {
        response = await client.sendMessageWithResponse('index.kick', {
          job_id: started.jobId,
        });
      } catch (err) {
        notifyIndexingRequestFailed({
          source,
          error: err?.message || getLocalizedMessage('catalog.update.startIndexingFailed'),
        });
        throw err;
      }
      if (response?.result === 'error') {
        const message = response?.error || getLocalizedMessage('catalog.update.startIndexingFailed');
        notifyIndexingRequestFailed({ source, error: message });
        throw new Error(message);
      }

      return started;
    },

    async getStatus(jobId) {
      const response = await client.sendMessageWithResponse('index.status', {
        job_id: String(jobId),
      });
      if (response?.result === 'error') {
        throw new Error(response?.error || 'Failed to load indexing status');
      }
      const indexing = response?.data?.indexing || response?.indexing || response?.data || {};
      return {
        jobId: String(indexing.job_id || response?.job_id || jobId),
        state: indexing.state || indexing.status || response?.status || 'unknown',
        progress: indexing.progress ?? response?.progress ?? null,
        error: indexing.error || indexing.error_summary || null,
        publishedGeneration: indexing.published_generation || null,
        publishedRevision: indexing.published_revision || null,
        indexing,
      };
    },
  };
}
